import { GoalType } from '@griply/types';
import React, { useCallback, useMemo, useState } from 'react';
import { DeleteGoalsDialog } from 'shared/dialogs/delete-goals';
import { useDeleteGoalsMutation } from 'shared/hooks/queries/use-delete-goals-mutation';
import { Goal } from 'shared/types/goal';

import {
  DeleteGoalsContext,
  DeleteGoalsContextProps,
} from './delete-goals-context';

export type DeleteGoalsProviderProps = {
  children: React.ReactNode;
};

export const DeleteGoalsProvider: React.FC<DeleteGoalsProviderProps> = ({
  children,
}) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const {
    mutate: deleteGoals,
    error,
    isPending: isLoading,
    reset,
  } = useDeleteGoalsMutation();

  const openDialog = useCallback((goalsToDelete: Goal[]) => {
    setGoals(goalsToDelete);
  }, []);

  const closeDialog = useCallback(() => {
    reset();
    setGoals([]);
  }, [reset]);

  const submit = useCallback(() => {
    if (goals.length) {
      deleteGoals(
        goals.map(({ id }) => id),
        {
          onSuccess: () => {
            closeDialog();
          },
        },
      );
    }
  }, [deleteGoals, goals, closeDialog]);

  const value = useMemo<DeleteGoalsContextProps>(
    () => ({ onDeleteGoals: openDialog }),
    [openDialog],
  );

  return (
    <DeleteGoalsContext.Provider value={value}>
      {children}
      <DeleteGoalsDialog
        open={!!goals.length}
        count={goals.length}
        isProject={
          !!goals.length && goals.every(({ type }) => type === GoalType.Project)
        }
        onClose={closeDialog}
        onSubmit={submit}
        onRetry={submit}
        isLoading={isLoading}
        isError={!!error}
      />
    </DeleteGoalsContext.Provider>
  );
};
